import type { TFunction } from 'i18next';

/**
 * How a campaign's own date range should be compared against the start/end dates
 * entered in the filters drawer.
 */
export enum EDateMatchMode {
  /** The campaign is running at some point during the range. */
  Overlaps = 'overlaps',
  /** The campaign starts and ends inside the range. */
  Within = 'within',
  /** The campaign's start date falls inside the range. */
  StartsIn = 'starts_in',
  /** The campaign's end date falls inside the range. */
  EndsIn = 'ends_in',
}

export const defaultDateMatchMode = EDateMatchMode.Overlaps;

export interface IDateMatchOption {
  id: EDateMatchMode;
  name: string;
}

export function dateMatchModeLabelMap(t: TFunction): Record<EDateMatchMode, string> {
  return {
    [EDateMatchMode.Overlaps]: t('Active during range'),
    [EDateMatchMode.Within]: t('Entirely within range'),
    [EDateMatchMode.StartsIn]: t('Starts during range'),
    [EDateMatchMode.EndsIn]: t('Ends during range'),
  };
}

export function dateMatchModeHelpMap(t: TFunction): Record<EDateMatchMode, string> {
  return {
    [EDateMatchMode.Overlaps]: t(
      'Matches campaigns running at any point in the range, including ones that started before it or end after it.',
    ),
    [EDateMatchMode.Within]: t('Matches only campaigns that both start and end inside the range.'),
    [EDateMatchMode.StartsIn]: t('Matches campaigns whose start date falls inside the range.'),
    [EDateMatchMode.EndsIn]: t('Matches campaigns whose end date falls inside the range.'),
  };
}

export function getDateMatchOptions(t: TFunction): IDateMatchOption[] {
  const labels = dateMatchModeLabelMap(t);
  return Object.values(EDateMatchMode).map((mode) => ({ id: mode, name: labels[mode] }));
}

export interface IDateMatchFilters {
  date_end__gte?: string;
  date_end__lte?: string;
  date_start__gte?: string;
  date_start__lte?: string;
}

/**
 * Translates the start/end dates and the selected match mode into the API's
 * comparison operators.
 *
 * Either bound may be omitted, in which case the range is open ended on that
 * side and only the operators that have a value are returned.
 */
export function getDateMatchFilters(
  mode: EDateMatchMode = defaultDateMatchMode,
  dateStart?: string,
  dateEnd?: string,
): IDateMatchFilters {
  switch (mode) {
    case EDateMatchMode.Within:
      // campaign.date_start >= start AND campaign.date_end <= end
      return { date_start__gte: dateStart, date_end__lte: dateEnd };

    case EDateMatchMode.StartsIn:
      // start <= campaign.date_start <= end
      return { date_start__gte: dateStart, date_start__lte: dateEnd };

    case EDateMatchMode.EndsIn:
      // start <= campaign.date_end <= end
      return { date_end__gte: dateStart, date_end__lte: dateEnd };

    /*
     * Overlaps, and the fallback for an unrecognized mode such as one restored from a
     * filter saved before this option existed:
     * campaign.date_start <= end AND campaign.date_end >= start
     */
    default:
      return { date_start__lte: dateEnd, date_end__gte: dateStart };
  }
}
